import type { SxProps, Theme } from "@mui/material";
import { DateTime } from "luxon";
import type { CSSProperties } from "react";
import type { DepartmentLocation, DepartmentType, NurseInShift, WeekData } from "@/utils/models";
import { gridCellStyle } from "../MasterSchedule/v2/utils";

export const topFiltersBarHeight = 30 as const satisfies CSSProperties["height"];

// Create a pseudo-location for unassigned nurses
export const unassignedLocation: DepartmentLocation = {
	id: "unassigned",
	department_id: "",
	location: "No Location",
	location_short_name: "No Location",
	roles: ["all"],
	is_charge: false,
	shift_type: "",
	section: "Unassigned",
	days: ["sun", "mon", "tue", "wed", "thu", "fri", "sat"], // All days active
	shift_start: "00:00:00",
	shift_end: "23:59:59",
	week_numbers: [],
	dayAmounts: {},
	amount: 1,
	amountRequired: 1,
};

// Type Definitions
export type DayNurseData = {
	dayIndex: number; // 0-6 (Sun-Sat)
	date: DateTime;
	nurses: NurseInShift[]; // All nurses for this location on this day
};

export type LocationRowData = {
	location: DepartmentLocation;
	weekData: DayNurseData[]; // 7 days
};

export type SynergyGridData = Record<string, LocationRowData[]>; // Keyed by section

// Styles
export const synergyNurseCellStyle = {
	...gridCellStyle,
	display: "flex",
	flexDirection: "column",
	gap: "2px",
	alignItems: "center",
	justifyContent: "start",
	padding: "2px 2px",
} as const satisfies SxProps<Theme>;

// Helper Functions

/**
 * Check if a day should be blocked for a location
 * @param date - The date to check
 * @param location - DepartmentLocation object
 * @returns true if the day is NOT in the location's configured days
 */
export function isDayBlockedForLocation(date: DateTime, location: DepartmentLocation): boolean {
	const dayName = date.toFormat("ccc").toLowerCase(); // "sun", "mon", "tue", etc.
	return !location.days.includes(dayName);
}

/**
 * Count assigned shifts for a location across the week
 * @param weekData - Array of DayNurseData for the location
 * @returns Number of nurses assigned across all days
 */
export function countAssignedShiftsForLocation(weekData: DayNurseData[]): number {
	return weekData.reduce((sum, day) => sum + day.nurses.length, 0);
}

function handleUnassignedNurseShifts(weekData: WeekData, matchedNurseShiftIds: Set<string>, sectionMap: SynergyGridData) {
	// Collect unassigned nurses (those without a location or not matched to any location)
	const unassignedDayDataMap = new Map<number, NurseInShift[]>();

	for (const shift of weekData.weeklyShifts) {
		for (let dayIndex = 0; dayIndex < shift.days.length; dayIndex++) {
			const day = shift.days[dayIndex];

			// Find nurses that weren't matched to any location
			const unassignedNurses = day.nurses.filter((nurse) => {
				// If already matched, skip
				if (matchedNurseShiftIds.has(nurse.id)) {
					return false;
				}
				// Include nurses without a location or with unrecognized locations
				return true;
			});

			if (unassignedNurses.length > 0) {
				if (!unassignedDayDataMap.has(dayIndex)) {
					unassignedDayDataMap.set(dayIndex, []);
				}
				unassignedDayDataMap.get(dayIndex)?.push(...unassignedNurses);
			}
		}
	}

	// If there are unassigned nurses, create an "Unassigned" section
	if (unassignedDayDataMap.size > 0) {
		const unassignedWeekData: DayNurseData[] = [];
		for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
			const nurses = unassignedDayDataMap.get(dayIndex) || [];
			const date = weekData.weeklyShifts[0]?.days[dayIndex]?.date || DateTime.now().plus({ day: dayIndex });

			unassignedWeekData.push({
				dayIndex,
				date,
				nurses,
			});
		}

		// Add to "Unassigned" section
		sectionMap.Unassigned = [
			{
				location: unassignedLocation,
				weekData: unassignedWeekData,
			},
		];
	}
}

/**
 * Build the synergy grid data structure from locations and week data
 * Maps department locations to actual nurse assignments for the week
 * Combines all shift types (Day/Night/etc) into one row per location
 *
 * @returns SynergyGridData object keyed by section name
 */
export function buildSynergyGridData(
	locations: DepartmentLocation[],
	weekData: WeekData | undefined,
	selectedTypes: (DepartmentType | "all")[],
): SynergyGridData {
	if (!weekData || !weekData.weeklyShifts || weekData.weeklyShifts.length === 0) {
		return {};
	}
	const isAll = selectedTypes.includes("all");
	const selectedTypeSet = new Set<DepartmentType>(selectedTypes.filter((t): t is DepartmentType => t !== "all"));
	const sectionMap: SynergyGridData = {};

	// Track matched nurse shift IDs to prevent duplicates
	const matchedNurseShiftIds = new Set<string>();

	const filteredLocations = isAll
		? locations
		: locations.filter((loc) => {
				const t = loc.departmentType;
				return t != null && selectedTypeSet.has(t);
			});

	// Process each location
	for (const location of filteredLocations) {
		const section = location.section || "Unassigned";

		// Initialize section if not exists
		if (!sectionMap[section]) {
			sectionMap[section] = [];
		}

		// Build 7-day data structure for this location
		// We need to combine all shift types (Day, Night, etc.) into one row
		const dayDataMap = new Map<number, NurseInShift[]>(); // dayIndex -> nurses

		// Collect nurses from ALL shift types for this location
		for (const shift of weekData.weeklyShifts) {
			for (let dayIndex = 0; dayIndex < shift.days.length; dayIndex++) {
				const day = shift.days[dayIndex];

				// Filter nurses that match this location AND haven't been matched yet
				const matchingNurses = day.nurses.filter((nurse) => {
					const nurseShiftId = nurse.id;
					// Check if this nurse shift was already matched to another location
					if (matchedNurseShiftIds.has(nurseShiftId)) {
						return false;
					}
					// Check if location matches
					return nurse.location === location.location;
				});

				// Mark these nurses as matched
				for (const nurse of matchingNurses) {
					matchedNurseShiftIds.add(nurse.id);
				}

				// Add to the day's nurse list
				if (!dayDataMap.has(dayIndex)) {
					dayDataMap.set(dayIndex, []);
				}
				dayDataMap.get(dayIndex)?.push(...matchingNurses);
			}
		}

		// Convert map to array of DayNurseData
		const weekDataArray: DayNurseData[] = [];
		for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
			const nurses = dayDataMap.get(dayIndex) || [];
			// Get the date from the first shift's day (they should all have the same dates)
			const date = weekData.weeklyShifts[0]?.days[dayIndex]?.date || DateTime.now().plus({ day: dayIndex });

			weekDataArray.push({
				dayIndex,
				date,
				nurses,
			});
		}

		// Add to section
		sectionMap[section].push({
			location,
			weekData: weekDataArray,
		});
	}
	if (isAll) {
		handleUnassignedNurseShifts(weekData, matchedNurseShiftIds, sectionMap);
	}
	return sectionMap;
}

/**
 * Get all unique sections from locations, sorted alphabetically
 * @param locations - Array of DepartmentLocation
 * @returns Array of unique section names
 */
export function getUniqueSections(locations: DepartmentLocation[]): string[] {
	const sections = new Set(locations.map((loc) => loc.section || "No Section"));
	return Array.from(sections).sort();
}
